import { Check, CircleMinus, Clock, X } from "lucide-react-native";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import type { MedicationLog } from "../hooks/useMedicationLogs";
import { isMedicationLogForOccurrence } from "../medication-log-date";
import { sameMedication } from "./sameMedication";

export { sameMedication } from "./sameMedication";
export type { DoseLogIdentity } from "./sameMedication";

export type DoseStatus = MedicationLog["status"];

/** The dose marks come from the theme, the one place they are defined. */
export const DOSE_STATUS_COLORS: Record<DoseStatus, string> =
  semanticColors.doseStatus;

export const DOSE_STATUS_ICONS: Record<DoseStatus, typeof Check> = {
  taken: Check,
  late: Clock,
  missed: X,
  skipped: CircleMinus,
};

function isDoseStatus(value: string | null | undefined): value is DoseStatus {
  return (
    value === "taken" ||
    value === "late" ||
    value === "missed" ||
    value === "skipped"
  );
}

/**
 * Find the latest dose-log entry for a medication row.
 *
 * Scheduled rows must match their exact scheduled occurrence. Legacy rows
 * without scheduled occurrence metadata fall back to today's medication log.
 */
export function getTodayDoseLog(
  logs: MedicationLog[] | undefined,
  medication: {
    medicationId?: string | null;
    medicationName?: string | null;
    scheduledFor?: string | null;
  },
  today: string,
  timezone?: string,
): MedicationLog | undefined {
  if (!logs || logs.length === 0) return undefined;

  const matches = logs.filter((log) => {
    if (!sameMedication(log, medication)) return false;
    return isMedicationLogForOccurrence(
      log,
      medication.scheduledFor,
      today,
      timezone,
    );
  });

  if (matches.length === 0) return undefined;

  return matches.reduce((a, b) =>
    new Date(a.takenAt).getTime() > new Date(b.takenAt).getTime() ? a : b,
  );
}

export function getTodayDoseStatus(
  logs: MedicationLog[] | undefined,
  medication: {
    medicationId?: string | null;
    medicationName?: string | null;
    scheduledFor?: string | null;
  },
  today: string,
  timezone?: string,
): DoseStatus | undefined {
  const latest = getTodayDoseLog(logs, medication, today, timezone);
  if (!latest) return undefined;
  return isDoseStatus(latest.status) ? latest.status : undefined;
}
